import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Injectable, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, HammerGestureConfig, HammerModule, HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DefaultUrlSerializer, RouterModule, UrlSerializer, UrlTree } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import * as Hammer from 'hammerjs';
import { NgxCurrencyModule } from 'ngx-currency';
import { PerfectScrollbarConfigInterface, PerfectScrollbarModule, PERFECT_SCROLLBAR_CONFIG } from 'ngx-perfect-scrollbar';
import { QuillModule } from 'ngx-quill';
import { environment } from '../environments/environment';
import { HeaderService } from '../services/header.service';
import { NavigationService } from '../services/navigation.service';
import { SnackBarService } from '../services/snack.bar.service';
import { SharedService } from './../services/shared.service';
import { AppComponent } from './app.component';
import { AppRoutes } from './app.routing';
import { DataService } from './data.service';
import { DemoMaterialModule } from './demo-material-module';
import { GraphQLModule } from './graphql.module';
import { AppBlankComponent } from './layouts/blank/blank.component';
import { FullComponent } from './layouts/full/full.component';
import { AppHeaderComponent } from './layouts/full/header/header.component';
import { AppSidebarComponent } from './layouts/full/sidebar/sidebar.component';
import { SubheaderComponent } from './layouts/full/subheader/subheader.component';
import { PopupMenuModule } from './shared/components/popup-menu/popup-menu.module';
import { ActivityDetailDialog } from './shared/dialog/activity-detail-dialog/activity-detail-dialog';
import { BottomSheet } from './shared/dialog/bottomSheet/bottom-sheet';
import { ConfirmationDialog } from './shared/dialog/confirmation-dialog/confirmation-dialog';
import { FilterContentDialog } from './shared/dialog/filter-content-dialog/filter-content-dialog';
import { PaymentDialog } from './shared/dialog/payment-dialog/payment-dialog';
import { QuotationDialog } from './shared/dialog/quotation-dialog/quotation-dialog';
import { SRListingFilterDialog } from './shared/dialog/sr-listing-filter-dialog/sr-listing-filter-dialog';
import { RemarkDialogComponent } from './shared/dialog/remark-dialog/remark-dialog';
import { SharedModule } from './shared/shared.module';
import { SpinnerComponent } from './shared/spinner.component';
import { ReplaceLineBreaksPipeModule } from '../app/shared/pipe/nl2br-pipe/nl2br-pipe.modules';
import { ACLService } from '../services/acl.service';
import { SwitchDialog } from './shared/dialog/switch-dialog/switch-dialog';
import { AccountDebtorService } from '../services/accountDebtor.service';
import { CryptoService } from '../services/crypto.service';
import { SocketIoService } from '../services/socketio.service';
import { PopupDialog } from './shared/dialog/popup-dialog/popup-dialog';
import { SanitizerPipeModule } from './shared/pipe/sanitizer-pipe/sanitizer-pipe.module';
import { Dialog1Template } from './template/dialog/dialog1-template/dialog1-template';
import { DialogHeader } from './template/dialog/dialog-header-template/dialog-header';
import { DynamicPipeModule } from './template/pipe/dynamic-pipe.module';
import { CustomDirectiveModule } from './shared/directives/directive.module';
import { Dialog2Template } from './template/dialog/dialog2-template/dialog2-template';
import { Dialog3Template } from './template/dialog/dialog3-template/dialog3-template';
import { Dialog4Template } from './template/dialog/dialog4-template/dialog4-template';
import { Dialog5Template } from './template/dialog/dialog5-template/dialog5-template';
import { Dialog6Template } from './template/dialog/dialog6-template/dialog6-template';
import { FooterComponent } from './layouts/full/footer/footer.component';
import { FooterService } from '../services/footer.service';
import { ProfileService } from '../services/profile.service';
import { PaymentConfirmDialog } from './shared/dialog/payment-confirm-dialog/payment-confirm-dialog';

@Injectable()
export class MyHammerConfig extends HammerGestureConfig {
    overrides = {
        // override hammerjs default configuration
        swipe: { direction: Hammer.DIRECTION_ALL }
    } as any;
}

export class LowerCaseUrlSerializer extends DefaultUrlSerializer {
    parse(url: string): UrlTree {

        if (url.includes('tenant-signup')) {

            const stringBeforeLastSlash = url.substring(0, url.lastIndexOf('/'));

            const n = url.lastIndexOf('/');
            const stringAfterLastSlash = url.substring(n + 1);

            return super.parse(stringBeforeLastSlash.toLowerCase() + "/" + stringAfterLastSlash);

        } else {
            const subStr = url.split("?");

            if (subStr.length > 1) {
                // only make url before query param to lower case
                // all query params remain allow CAPS
                return super.parse(subStr[0].toLowerCase() + "?" + subStr[1]);
            }

            return super.parse(url.toLowerCase());
        }

    }
}

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
    suppressScrollX: true,
    wheelSpeed: 2,
    wheelPropagation: true
};

@NgModule({
    entryComponents: [
        ConfirmationDialog,
        BottomSheet,
        FilterContentDialog,
        PaymentDialog,
        QuotationDialog,
        SRListingFilterDialog,
        ActivityDetailDialog,
        RemarkDialogComponent,
        SwitchDialog,
        PaymentConfirmDialog
    ],
    declarations: [
        AppComponent,
        FullComponent,
        AppHeaderComponent,
        SpinnerComponent,
        AppBlankComponent,
        AppSidebarComponent,
        ConfirmationDialog,
        BottomSheet,
        SubheaderComponent,
        FilterContentDialog,
        PaymentDialog,
        QuotationDialog,
        SRListingFilterDialog,
        ActivityDetailDialog,
        RemarkDialogComponent,
        SwitchDialog,
        PopupDialog,
        Dialog1Template,
        Dialog2Template,
        Dialog3Template,
        Dialog4Template,
        Dialog5Template,
        Dialog6Template,
        DialogHeader,
        FooterComponent,
        PaymentConfirmDialog
    ],
    imports: [
        CommonModule,
        BrowserModule,
        BrowserAnimationsModule,
        DemoMaterialModule,
        NgxCurrencyModule,
        FormsModule,
        FlexLayoutModule,
        HttpClientModule,
        PerfectScrollbarModule,
        ReactiveFormsModule,
        SharedModule,
        PopupMenuModule,
        HammerModule,
        QuillModule.forRoot({ theme: 'snow' }),
        RouterModule.forRoot(AppRoutes, { scrollPositionRestoration: 'top' }),
        ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
        GraphQLModule,
        ReplaceLineBreaksPipeModule,
        SanitizerPipeModule,
        DynamicPipeModule,
        CustomDirectiveModule
    ],
    providers: [
        {
            provide: PERFECT_SCROLLBAR_CONFIG,
            useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
        },
        SharedService,
        SnackBarService,
        NavigationService,
        HeaderService,
        ACLService,
        AccountDebtorService,
        CryptoService,
        SocketIoService,
        {
            provide: HAMMER_GESTURE_CONFIG,
            useClass: MyHammerConfig
        },
        DataService,
        {
            provide: UrlSerializer,
            useClass: LowerCaseUrlSerializer
        },
        FooterService,
        ProfileService
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }
